import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import {
  ResizeHandle,
  handleEdgeForPaneOn,
  useResizablePane,
} from '../useResizablePane';
import SegmentMap from './SegmentMap';
import SiteMap from './SiteMap';
import SitePageDock from './SitePageDock';
import {
  balanceLabel,
  crossingShare,
  graphState,
  linkCount,
  scopesForSite,
  siteColor,
  siteKey,
  sourceHex,
} from './siteGraph';
import type { Segment } from './siteGraph';
import {
  EMPTY_PAIR,
  closeColumn,
  initialPair,
  isPairEmpty,
  prunePair,
  segmentFocus,
  selectSite,
} from './sitePairing';
import type { DockPair } from './sitePairing';
import type { DropMode } from './linkFacts';
import type {
  ExternalPlacement,
  ExternalTarget,
  LinkEdge,
  LinkMapPage,
  SiteGraph,
  SiteGraphSegment,
} from '../../types';

/**
 * The site view: the whole map as a shape, and one ribbon at a time in detail.
 *
 * THE TREE CANNOT ANSWER THIS. It renders a grouped adjacency list — a page
 * once, its out-edges as leaves — which is correct and shapeless. "Which of my
 * sites is starved" is not a question you can ask 174 rows.
 *
 * Click-to-drill rather than hover-to-drill: the detail list is long enough to
 * read, and a panel that vanishes when the pointer leaves the ribbon cannot be
 * read at all.
 */

interface Props {
  mapId: number;
  /** One fetch feeds the graph and the dock both — every page carries its
   *  `site_id`, so the columns need no request of their own. */
  pages: LinkMapPage[];
  edges: LinkEdge[];
  /** The map's scope ids, joined. The site list is derived from which PAGES the
   *  map covers, so it changes when a source is added or removed even though no
   *  edge does — removing a source with no links moved nothing this panel was
   *  watching, and its site sat on the graph until a reload. */
  sourcesKey: string;
  targets: ExternalTarget[];
  externalPlacements: ExternalPlacement[];
  onPlace: (externalTargetId: number, pageId: number) => Promise<void>;
  onUnplace: (placement: ExternalPlacement) => Promise<void>;
  /** Set while a subject chip is held down, so the graph can say which sites
   *  that subject feeds. */
  litSites?: Set<string> | null;
  litColor?: string;
  litLabel?: string;
  onClearLit?: () => void;
  /** scope id → its colour order, so a site's feeder dots wear the same colours
   *  as the chips they name. */
  scopeOrder: Map<number, number>;
  truncated?: boolean;
  dropMode: DropMode;
  onDropModeChange: (mode: DropMode) => void;
  /** `ownerId` GAINS the link and points at `targetId`. */
  onLink: (ownerId: number, targetId: number) => Promise<void>;
  /** The tree's own verbs, so a link can be undone where it was made. */
  onUnlink: (edge: LinkEdge) => Promise<void>;
  onToggleLock: (edge: LinkEdge) => Promise<void>;
  /** Open a page itself — its deployed address, when it has one. */
  onOpenPage?: (pageId: number) => void;
}

export default function SiteGraphPanel({
  mapId,
  pages,
  edges,
  sourcesKey,
  targets,
  externalPlacements,
  onPlace,
  onUnplace,
  litSites,
  litColor,
  litLabel,
  onClearLit,
  scopeOrder,
  truncated,
  dropMode,
  onDropModeChange,
  onLink,
  onUnlink,
  onToggleLock,
  onOpenPage,
}: Props) {
  const [graph, setGraph] = useState<SiteGraph | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Segment | null>(null);
  const [segment, setSegment] = useState<SiteGraphSegment | null>(null);
  const [segmentLoading, setSegmentLoading] = useState(false);
  const [pair, setPair] = useState<DockPair>(EMPTY_PAIR);
  // Global, not per map: how tall you like the working area is a preference
  // about the screen, not about one link map.
  const dock = useResizablePane({
    storageKey: 'linkbuilder:site-dock-height',
    initial: 280,
    min: 140,
    max: 520,
    handleOn: handleEdgeForPaneOn('bottom'),
    // Folds to the toolbar row, so the drop-mode control and any notice stay
    // readable — a pane that folds to nothing gives you no way back that is not
    // the handle itself.
    collapsedSize: 40,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setGraph(await api.getLinkMapSiteGraph(mapId));
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not build the site graph');
    } finally {
      setLoading(false);
    }
  }, [mapId]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Open with two sites already showing, and afterwards drop any column whose
   * site has left the map.
   *
   * ONE effect for both, because they are the same decision made at two moments
   * and they would otherwise fight: seeding runs when the graph arrives, and
   * pruning can empty the pair — at which point a separate seeding effect would
   * helpfully re-open the dock you just closed.
   *
   * `seeded` is per map, so switching maps opens the new one on its own busiest
   * pair rather than inheriting the last one's columns.
   */
  const seeded = useRef<number | null>(null);
  useEffect(() => {
    if (!graph) return;
    if (seeded.current !== mapId) {
      seeded.current = mapId;
      setPair(initialPair(graph.sites, graph.flows));
      return;
    }
    const live = graph.sites.map((s) => s.site_id);
    setPair((p) => prunePair(p, live));
  }, [graph, mapId]);

  /**
   * Keep the graph in step with edges changed OUTSIDE this panel.
   *
   * Clear links, Relink, Lock all and Undo all live in the map header, and the
   * graph is a server-side aggregation of exactly what they change. Without
   * this, clearing every link leaves the arrows on screen — the picture and the
   * map disagree until the panel is remounted.
   *
   * Keyed on the edge SET, not a count: a relink can replace every edge and
   * leave the total identical.
   */
  const stamp = useMemo(
    () => `${sourcesKey}|${edges.map((e) => e.id).join(',')}`,
    [sourcesKey, edges],
  );
  const lastStamp = useRef<string | null>(null);
  useEffect(() => {
    // The first run is the mount, which `load` above already covers.
    if (lastStamp.current === null) {
      lastStamp.current = stamp;
      return;
    }
    if (lastStamp.current === stamp) return;
    lastStamp.current = stamp;
    api.getLinkMapSiteGraph(mapId).then(setGraph).catch(() => {
      // The counts go stale rather than the panel breaking; `load` will fix it.
    });
  }, [stamp, mapId]);

  const pick = async (seg: Segment) => {
    setSelected(seg);
    // An arrow names two sites; putting them in the dock makes the thing you
    // just inspected the thing you can act on, instead of a second panel that
    // competes with the one node clicks open.
    setPair({ left: seg.fromSiteId, right: seg.toSiteId });
    setSegment(null);
    setSegmentLoading(true);
    try {
      setSegment(await api.getLinkMapSegment(mapId, seg.fromSiteId, seg.toSiteId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load those links');
    } finally {
      setSegmentLoading(false);
    }
  };

  if (loading) {
    return <p className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading…</p>;
  }
  if (error) {
    return (
      <div className="m-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-2 rounded text-sm">
        {error}
      </div>
    );
  }

  const state = graphState(graph);

  // ONLY "no sources" hides the graph.
  //
  // It used to hide it whenever there were no links, or only one site, on the
  // grounds that there was "no shape to show". That was wrong in a way that
  // only mattered once this became the surface you WORK on: you open the pages
  // by clicking a node, so hiding the nodes until links exist makes it
  // impossible to create the first one — and Clear links dropped you straight
  // into that dead end.
  //
  // A site with no links is also the most useful thing this view can tell you,
  // and one site alone still links to itself, which is most of any real map.
  if (state === 'no-sources') {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center max-w-sm">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            This map has no sources yet.
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Add a subject and its sites appear here, ready to link.
          </p>
        </div>
      </div>
    );
  }
  if (!graph) return null;

  const share = crossingShare(graph);

  return (
    // A COLUMN, not one scrolling block. The dock is a pane with a height you
    // set, so everything above it has to be able to give that height up —
    // which means the graph and the legend scroll inside their own region
    // rather than pushing the dock off the bottom of the screen.
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="px-4 pt-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {graph.total_links === 0 ? (
            <>
              <span className="font-medium">{graph.sites.length} sites</span>, no
              links between them yet.
            </>
          ) : (
            <>
              <span className="font-medium">{linkCount(graph.crossing_links)}</span>{' '}
              of {graph.total_links} cross between sites
              {share ? ` - ${share}` : ''}.
            </>
          )}
        </p>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          {graph.total_links === 0
            ? 'Drag a page from one list below onto a page in the other to link them. Click a site to swap a list.'
            : 'Each arrow points from the site that links to the site it links to. Click a site to load its pages, or an arrow to see the links.'}
        </p>
      </div>

      {litSites && (
        <div className="mx-4 mb-1 px-2 py-1 rounded text-xs flex items-center gap-2"
             style={{ backgroundColor: `${litColor}1a`, color: litColor }}>
          <span className="truncate">
            {litLabel} has pages on{' '}
            {litSites.size === 1 ? 'this site' : `these ${litSites.size} sites`}.
          </span>
          <button
            onClick={onClearLit}
            className="ml-auto shrink-0 underline hover:no-underline"
          >
            Done
          </button>
        </div>
      )}

      <div className="px-4 py-2">
        <SiteMap
          graph={graph}
          mapId={mapId}
          onSelect={pick}
          selected={selected}
          onSelectSite={(siteId) => {
            // A node click is a new question. Leaving the arrow's focus in
            // place would narrow the column you just opened to the last
            // segment's pages.
            setSelected(null);
            setSegment(null);
            setPair((p) => selectSite(p, siteId));
          }}
          pair={pair}
          litSites={litSites}
          litColor={litColor}
        />
      </div>

      {/* Every site is a node now — a force layout has somewhere to put a
          disconnected one — so the legend covers them all, and "no links either
          way" is a real finding rather than a filler row. */}
      <div className="px-4 pb-3 grid gap-1.5 sm:grid-cols-2">
        {graph.sites.map((site, i) => (
          <div
            key={siteKey(site.site_id)}
            className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300"
          >
            <span
              className="w-2.5 h-2.5 rounded-sm shrink-0"
              style={{ backgroundColor: siteColor(i) }}
            />
            <span className="font-medium truncate">{site.name}</span>
            {/* WHICH SUBJECTS FEED IT. A site can be fed by several, so
                removing one chip often leaves the node in place — and without
                this that is indistinguishable from a panel that failed to
                refresh, which is what anyone will assume. */}
            {(() => {
              const feeders = scopesForSite(pages, site.site_id);
              if (feeders.length === 0) return null;
              return (
                <span
                  className="shrink-0 inline-flex items-center gap-0.5"
                  title={`Fed by ${feeders.length} subject${
                    feeders.length === 1 ? '' : 's'
                  }`}
                >
                  {feeders.map((scopeId) => (
                    <span
                      key={scopeId}
                      className="w-1.5 h-1.5 rounded-full"
                      style={{
                        backgroundColor: sourceHex(
                          scopeOrder.get(scopeId) ?? scopeId,
                        ),
                      }}
                    />
                  ))}
                </span>
              );
            })()}
            <span className="text-gray-400 dark:text-gray-500 truncate">
              {site.page_count} pages · {balanceLabel(site)}
            </span>
          </div>
        ))}
      </div>

      {selected && (
        <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {selected.fromName} → {selected.toName}
              <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                {linkCount(selected.count)}
              </span>
            </p>
            <button
              onClick={() => {
                setSelected(null);
                setSegment(null);
              }}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              Clear
            </button>
          </div>

          {segmentLoading ? (
            <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">Loading…</p>
          ) : segment ? (
            <>
              {/* The same graph one level down. The list below it stays because
                  it carries what a picture cannot: the anchor text that will
                  actually ship. */}
              <div className="mt-2 overflow-x-auto">
                <SegmentMap
                  segment={selected}
                  edges={segment.edges}
                  onOpenPage={onOpenPage}
                />
              </div>
              {segment.truncated && (
                // Never a silent cut — a list that stops reads as the whole
                // segment, and someone would count from it.
                <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                  Showing {segment.edges.length} of {segment.total}.
                </p>
              )}
              <ul className="mt-2 space-y-1">
                {segment.edges.map((edge) => (
                  <li
                    key={edge.id}
                    className="flex items-baseline gap-2 text-xs py-1 border-b border-gray-100 dark:border-gray-800 last:border-0"
                  >
                    <button
                      onClick={() => onOpenPage?.(edge.page_id)}
                      className="font-medium text-gray-700 dark:text-gray-200 hover:text-blue-700 dark:hover:text-blue-300 truncate max-w-[40%] text-left"
                      title={edge.source_title}
                    >
                      {edge.source_title}
                    </button>
                    <span className="text-gray-400 shrink-0">
                      {edge.reciprocal ? '↔' : '→'}
                    </span>
                    <button
                      onClick={() => onOpenPage?.(edge.linked_page_id)}
                      className="text-gray-700 dark:text-gray-200 hover:text-blue-700 dark:hover:text-blue-300 truncate max-w-[40%] text-left"
                      title={edge.target_title}
                    >
                      {edge.target_title}
                    </button>
                    {/* The anchor is what actually ships. A segment list that
                        showed only page names could not reveal a link about to
                        go out with no anchor at all. */}
                    <span className="ml-auto text-gray-400 dark:text-gray-500 truncate max-w-[30%] italic">
                      {edge.anchor_text || 'no anchor'}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      )}
      </div>

      {!isPairEmpty(pair) && (
        <>
          {/* The line between what you are looking at and what you are working
              on. Dragging it up gives the page lists room as the map fills in;
              a CLICK folds them away when the graph is the thing you want —
              `useResizablePane` tells the two gestures apart. */}
          <ResizeHandle
            axis="y"
            grabber
            dragging={dock.dragging}
            onPointerDown={dock.onPointerDown}
            title={
              dock.collapsed
                ? 'Drag to resize, or click to show the pages again'
                : 'Drag to resize, or click to fold the pages away'
            }
          />
          <div
            className="shrink-0 min-h-0 flex flex-col"
            style={{ height: dock.width }}
          >
          <SitePageDock
            pair={pair}
            sites={graph.sites}
            pages={pages}
            edges={edges}
            truncated={truncated}
            dropMode={dropMode}
            onDropModeChange={onDropModeChange}
            onUnlink={async (edge) => {
              await onUnlink(edge);
              setGraph(await api.getLinkMapSiteGraph(mapId));
            }}
            onToggleLock={onToggleLock}
            // Only once the segment has LOADED: focusing on an empty list
            // mid-fetch would blank both columns and read as a broken click.
            focus={segment ? segmentFocus(segment.edges) : null}
            focusLabel={
              selected ? `${selected.fromName} → ${selected.toName}` : undefined
            }
            onClearFocus={() => {
              setSelected(null);
              setSegment(null);
            }}
            onClose={(side) => setPair((p) => closeColumn(p, side))}
            onLink={async (ownerId, targetId) => {
              await onLink(ownerId, targetId);
              // The arrow above has to move with the link. Without this the
              // graph keeps showing the old count while the dock shows the new
              // link, and the two disagree until the panel is reopened.
              setGraph(await api.getLinkMapSiteGraph(mapId));
            }}
            onOpenPage={onOpenPage}
            targets={targets}
            external={externalPlacements}
            onPlace={onPlace}
            onUnplace={async (placement) => {
              await onUnplace(placement);
            }}
          />
          </div>
        </>
      )}
    </div>
  );
}
